"use client";

import { useQuery } from "@tanstack/react-query";

export interface KalshiLiveCompetitorData {
  id?: string;
  roundScores?: number[];
  currentRoundScore?: string;
  overallScore?: number;
  serving?: boolean;
  seed?: number;
}

export interface KalshiLiveData {
  milestoneId: string;
  type: string;
  status?: string;
  widgetStatus?: string;
  homePoints?: number;
  awayPoints?: number;
  clock?: string;
  period?: number;
  periodLabel?: string;
  winner?: string;
  lastPlay?: string;
  competitors?: KalshiLiveCompetitorData[];
  raw?: {
    details?: Record<string, any>;
    [key: string]: any;
  };
}

async function fetchKalshiLiveData(eventTicker: string): Promise<KalshiLiveData | null> {
  const res = await fetch(`/api/markets/kalshi/live/${encodeURIComponent(eventTicker)}`);
  if (!res.ok) return null;
  return res.json();
}

/**
 * Polls live sports data for a Kalshi event. Refreshes every 5s while the game
 * is in progress, every 30s otherwise. Pauses when the tab is hidden —
 * otherwise every backgrounded tab keeps spamming the endpoint while the user
 * isn't looking at it.
 */
export function useKalshiLiveData(eventTicker: string | null | undefined, enabled = true) {
  return useQuery<KalshiLiveData | null>({
    queryKey: ["kalshi", "live", eventTicker],
    queryFn: () => fetchKalshiLiveData(eventTicker as string),
    enabled: enabled && !!eventTicker,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data?.widgetStatus === "in_progress" || data?.status === "inprogress") return 5000;
      return 30_000;
    },
    refetchIntervalInBackground: false,
    staleTime: 2_000,
    gcTime: 60_000,
  });
}
